import { getLocation } from "../state/modules/router/selectors";
import {
  getAllAppliedRefinements,
  getRecommendationsAnalytics,
} from "../state/modules/search";
import {
  isFacetPriceRange,
  getNextPriceRange,
  getPrevPriceRange,
} from "../state/modules/search/domains/priceRange/selectors";
import { getSearchTerm } from "../state/modules/facet/selectors";
import {
  TRACKING_KEY_PRODUCT_CLICK,
  TRACKING_CONTEXT_PRODUCT_CLICK,
  TRACKING_KEY_LOYALTY_OPT_IN_CLICK,
  TRACKING_KEY_LOYALTY_OPT_IN_REJECT,
  TRACKING_KEY_SAVE_FOR_LATER,
  TRACKING_CONTEXT_SAVE_FOR_LATER,
} from "./constants";
import {
  getClientPageLoadData,
  getPageSpecificAnalyticsData,
  getAppliedRefinementsData,
  getSharedAnalyticsObject,
  getListType,
} from "./pageLoad";
import { getCurrencyCode } from "../state/modules/config/selectors";

import { getViewItemListState } from "./getViewItemListState";

import {
  ANALYTICS_CLEAR_REFINEMENT,
  ANALYTICS_CLEAR_ALL_REFINEMENTS,
  ANALYTICS_LOAD_MORE,
  ANALYTICS_LOAD_PREVIOUS,
  ANALYTICS_OPEN_REFINEMENTS_SMALL,
  ANALYTICS_OPEN_REFINEMENT_TYPE,
  ANALYTICS_SELECT_ALL,
  ANALYTICS_REFINEMENTS_SELECT,
  ANALYTICS_REFINEMENTS_DESELECT,
  ANALYTICS_SAVE_FOR_LATER,
  ANALYTICS_PRODUCT_CLICK,
  ANALYTICS_PAGE_LOAD,
  ANALYTICS_REFINEMENTS_DONE,
  ANALYTICS_REFINEMENTS_SORT,
  ANALYTICS_REFINEMENTS_VIEW_ITEMS,
  ANALYTICS_REFINEMENTS_CLOSE_MOBILE,
  ANALYTICS_CLOSE_REFINEMENT_TYPE,
  ANALYTICS_LOYALTY_OPT_IN_CLICK,
  ANALYTICS_LOYALTY_OPT_IN_REJECT,
  ANALYTICS_VIEW_LIST_ITEM,
} from "../state/modules/analytics/constants";

import { isLargeMediumViewPort } from "../state/modules/search/domains/ui/helpers/viewportHelper";
import getCustomerId from "../client/identity/getCustomerId";
import getProductTreatment from "../helpers/getProductTreatment";
import { pushToDataLayerSitechromeWrapper as pushToDataLayer } from "@asosteam/asos-web-datalayer";
import {
  buildItemsDataTemplate,
  buildEcommerceItems,
} from "../helpers/analytics";

const emitterEvents = {};

export const sendViewItemListEvent = async (
  state,
  currentPageNumber,
  returnedProductList,
) => {
  const clientPageLoadData = await getClientPageLoadData(state);
  const pageSpecificAnalyticsData = getPageSpecificAnalyticsData(state);
  const appliedRefinementsData = await getAppliedRefinementsData(state, true);

  const viewItemListEvent = buildViewItemListEvent(
    pageSpecificAnalyticsData,
    clientPageLoadData,
    appliedRefinementsData,
    currentPageNumber,
    returnedProductList,
  );

  if (viewItemListEvent) {
    pushToDataLayer({
      view_item_list: viewItemListEvent,
    });
  }
};

const buildRecsAnalytics = (state) => {
  const { personalisationStatus, numberOfItems: numberOfRecommendedItems } =
    getRecommendationsAnalytics(state);

  return {
    personalisationStatus,
    numberOfRecommendedItems,
  };
};

const buildPaginationEvent = ({ state, pageNumber, returnedProductList }) => {
  const { query } = getLocation(state);

  return [
    "tracking.productList.paginate",
    {
      pageNumber,
      context:
        query.refine || query.sort ? "paginate with refinement" : "paginate",
      returnedProductList,
      refinements: {
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
      ...getSharedAnalyticsObject(state),
      ...buildRecsAnalytics(state),
    },
  ];
};

const buildRefinementEvent = (customEventPayload, actionPayload) => {
  const eventType = "tracking.productList.refine.refinements";
  const { returnedProductList, totalStyles } = actionPayload;

  const commonPayload = { returnedProductList, totalStyles };
  const eventPayload = { ...commonPayload, ...customEventPayload };

  return [eventType, eventPayload];
};

export const buildAppliedRefinement = (
  state,
  facet,
  facetValues,
  actionType,
) => {
  const appliedRefinement = {
    refinementId: facet.id,
    refinementName: facet.name,
  };
  if (isFacetPriceRange(facet)) {
    const actionsUsingPrevPriceRange = [ANALYTICS_CLEAR_ALL_REFINEMENTS];
    if (isLargeMediumViewPort(state)) {
      actionsUsingPrevPriceRange.push(ANALYTICS_CLEAR_REFINEMENT);
    }
    const { leftIndicator: min, rightIndicator: max } =
      actionsUsingPrevPriceRange.includes(actionType)
        ? getPrevPriceRange(state)
        : getNextPriceRange(state);

    return { ...appliedRefinement, min, max };
  } else {
    return {
      ...appliedRefinement,
      selections: facetValues.map((facetValue) => ({
        selectionId: facetValue.id,
        selectionName: facetValue.name,
      })),
    };
  }
};

const addToWishListEvent = (product) => ({
  add_to_wishlist: {
    ecommerce: {
      currency: "GBP",
      localCurrencyCode: product.currencyCode,
      valueLocalCurrency: product.priceLocalCurrency,
      items: [buildItemsDataTemplate(product, 0)],
    },
  },
});

const buildViewItemListEvent = (
  getPageSpecificAnalyticsData,
  getClientPageLoadData,
  getAppliedRefinementsData,
  currentPageNumber,
  productList,
) => {
  const {
    breadcrumbs: breadCrumb,
    categoryID,
    pageType,
    searchTerm: search_term,
    searchType,
    floor,
    isBrand,
    guidedSearch,
  } = getPageSpecificAnalyticsData;

  const {
    pageGridSize,
    pageNumber,
    pageSize,
    totalStyles,
    currency: localCurrencyCode,
    returnedProductList,
    requestId,
  } = getClientPageLoadData;

  if (
    (!returnedProductList || returnedProductList.length === 0) &&
    (!productList || productList.length === 0)
  ) {
    return null;
  }

  const { refinements } = getAppliedRefinementsData;

  const { listAppliedRefinements, sort } = refinements;

  // When currentPageNumber is undefined (e.g., ANALYTICS_PAGE_LOAD), fall back to pageNumber from page load data
  const currentPage = currentPageNumber >= 1 ? currentPageNumber : pageNumber;

  const viewItemListState = getViewItemListState({
    pageType,
    searchTerm: search_term,
    currentPageNumber: currentPage,
    hasSort: Boolean(sort?.sortType),
    hasAppliedRefinements: listAppliedRefinements?.length > 0,
  });

  const eventTemplate = {
    item_list_name: getListType({ pageType, isBrand }),
    item_list_state: viewItemListState,
    search_term,
    searchType,
    pageGridSize,
    pageSize: productList ? productList.length : pageSize,
    pageNumber: currentPage ?? 1,
    totalStyles,
    breadCrumb,
    refinements,
    ecommerce: {
      currency: "GBP",
      localCurrencyCode,
      items: buildEcommerceItems(returnedProductList, productList),
    },
    ...(guidedSearch && { guidedSearch }),
    ...(floor && { floor }),
    ...(categoryID && { categoryID }),
    ...(requestId && { request_id: requestId }),
  };

  return eventTemplate;
};

const buildSaveForLaterEvent = ({
  productId,
  reducedPrice,
  price,
  ...rest
}) => [
  TRACKING_KEY_SAVE_FOR_LATER,
  {
    context: TRACKING_CONTEXT_SAVE_FOR_LATER,
    productIID: productId,
    priceLocalCurrency: reducedPrice || price,
    ...rest,
  },
];

const buildProductClickEvent = ({
  carouselCatId,
  advertId,
  customerId,
  featuredProductTileType,
}) => [
  TRACKING_KEY_PRODUCT_CLICK,
  {
    context: TRACKING_CONTEXT_PRODUCT_CLICK,
    carouselCatId,
    advertId,
    customerId,
    featuredProductTileType,
  },
];

emitterEvents[ANALYTICS_LOAD_MORE] = async (action, state) => {
  const {
    payload: { returnedProductList, page },
  } = action;
  await sendViewItemListEvent(state, page, returnedProductList);

  return buildPaginationEvent({
    state,
    pageNumber: page,
    returnedProductList,
  });
};

emitterEvents[ANALYTICS_VIEW_LIST_ITEM] = async (_, state) => {
  await sendViewItemListEvent(state);
};

emitterEvents[ANALYTICS_LOAD_PREVIOUS] = async (action, state) => {
  const {
    payload: { returnedProductList, page },
  } = action;

  await sendViewItemListEvent(state, page, returnedProductList);

  return buildPaginationEvent({
    state,
    pageNumber: page,
    returnedProductList,
  });
};

emitterEvents[ANALYTICS_OPEN_REFINEMENTS_SMALL] = (action, state) =>
  buildRefinementEvent(
    {
      refinements: {
        context: "refinements:open",
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
    },
    action.payload,
  );

emitterEvents[ANALYTICS_CLEAR_REFINEMENT] = (action, state) => {
  const { facet, clearedFacetValues } = action.payload;

  return buildRefinementEvent(
    {
      refinements: {
        context: "refinements:clear",
        listAppliedRefinements: [
          buildAppliedRefinement(state, facet, clearedFacetValues, action.type),
        ],
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
      ...buildRecsAnalytics(state),
      ...getSharedAnalyticsObject(state),
    },
    action.payload,
  );
};

emitterEvents[ANALYTICS_REFINEMENTS_DONE] = (action, state) => {
  const { facet, selectedFacetValues } = action.payload;

  return buildRefinementEvent(
    {
      refinements: {
        context: "refinements:done",
        listAppliedRefinements: [
          buildAppliedRefinement(state, facet, selectedFacetValues),
        ],
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
      ...buildRecsAnalytics(state),
      ...getSharedAnalyticsObject(state),
    },
    action.payload,
  );
};

emitterEvents[ANALYTICS_REFINEMENTS_VIEW_ITEMS] = (action, state) => {
  const { facet, selectedFacetValues } = action.payload;

  return buildRefinementEvent(
    {
      refinements: {
        context: "refinements:close:viewItems",
        listAppliedRefinements: facet
          ? [buildAppliedRefinement(state, facet, selectedFacetValues)]
          : [],
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
      ...buildRecsAnalytics(state),
      ...getSharedAnalyticsObject(state),
    },
    action.payload,
  );
};

emitterEvents[ANALYTICS_REFINEMENTS_CLOSE_MOBILE] = (action, state) =>
  buildRefinementEvent(
    {
      refinements: {
        context: "refinements:close:non-panel",
        listAppliedRefinements: [],
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
    },
    action.payload,
  );

emitterEvents[ANALYTICS_SELECT_ALL] = (action, state) => {
  const facet = action.payload;

  const selectedFacetValues = facet.facetValues.filter(
    (facetValue) => facetValue.count > 0,
  );

  return buildRefinementEvent(
    {
      refinements: {
        context: "refinements:select:all",
        listAppliedRefinements: [
          buildAppliedRefinement(state, facet, selectedFacetValues),
        ],
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
      ...buildRecsAnalytics(state),
      ...getSharedAnalyticsObject(state),
    },
    action.payload,
  );
};

emitterEvents[ANALYTICS_CLEAR_ALL_REFINEMENTS] = (action, state) => {
  const { facets } = action.payload;

  const facetsWithSelectedValues = facets.filter(
    (facet) => facet.hasSelectedValues,
  );

  return buildRefinementEvent(
    {
      refinements: {
        context: "refinements:clear:all",
        listAppliedRefinements: facetsWithSelectedValues.map((facet) =>
          buildAppliedRefinement(
            state,
            facet,
            facet.facetValues.filter((facetValue) => facetValue.isSelected),
            action.type,
          ),
        ),
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
      ...buildRecsAnalytics(state),
      ...getSharedAnalyticsObject(state),
    },
    action.payload,
  );
};

emitterEvents[ANALYTICS_OPEN_REFINEMENT_TYPE] = (action, state) =>
  buildRefinementEvent(
    {
      refinements: {
        context: "refinements:open:refinementType",
        listAppliedRefinements: [
          {
            refinementId: action.payload.id,
            refinementName: action.payload.name,
          },
        ],
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
    },
    action.payload,
  );

emitterEvents[ANALYTICS_CLOSE_REFINEMENT_TYPE] = (action, state) =>
  buildRefinementEvent(
    {
      refinements: {
        context: "refinements:close",
        listAppliedRefinements: [
          {
            refinementId: action.payload.id,
            refinementName: action.payload.name,
          },
        ],
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
    },
    action.payload,
  );

emitterEvents[ANALYTICS_REFINEMENTS_SORT] = async (action, state) =>
  buildRefinementEvent(
    {
      refinements: {
        context: "refinements:sort",
        sort: {
          sortType: action.payload.type,
          sortName: action.payload.name,
        },
        listAllAppliedRefinements: getAllAppliedRefinements(state),
      },
      ...buildRecsAnalytics(state),
      ...getSharedAnalyticsObject(state),
    },
    action.payload,
  );

emitterEvents[ANALYTICS_REFINEMENTS_SELECT] = (action, state) => {
  const { facet, facetValue } = action.payload;

  return buildRefinementEvent(
    {
      refinements: {
        context: "refinements:select",
        listAppliedRefinements: [
          buildAppliedRefinement(state, facet, [facetValue]),
        ],
        listAllAppliedRefinements: getAllAppliedRefinements(state),
        filtered: !!getSearchTerm(state),
      },
      ...buildRecsAnalytics(state),
      ...getSharedAnalyticsObject(state),
    },
    action.payload,
  );
};

emitterEvents[ANALYTICS_REFINEMENTS_DESELECT] = (action, state) => {
  const { facet, facetValue } = action.payload;

  return buildRefinementEvent(
    {
      refinements: {
        context: "refinements:deselect",
        listAppliedRefinements: [
          buildAppliedRefinement(state, facet, [facetValue]),
        ],
        listAllAppliedRefinements: getAllAppliedRefinements(state),
        filtered: !!getSearchTerm(state),
      },
      ...buildRecsAnalytics(state),
      ...getSharedAnalyticsObject(state),
    },
    action.payload,
  );
};

emitterEvents[ANALYTICS_SAVE_FOR_LATER] = async (action, state) => {
  const currencyCode = getCurrencyCode(state);

  pushToDataLayer(addToWishListEvent({ ...action.payload, currencyCode }));

  return buildSaveForLaterEvent({ ...action.payload, currencyCode });
};

emitterEvents[ANALYTICS_LOYALTY_OPT_IN_CLICK] = () => [
  TRACKING_KEY_LOYALTY_OPT_IN_CLICK,
  {},
];

emitterEvents[ANALYTICS_LOYALTY_OPT_IN_REJECT] = () => [
  TRACKING_KEY_LOYALTY_OPT_IN_REJECT,
  {},
];

emitterEvents[ANALYTICS_PRODUCT_CLICK] = async (action, state) => {
  const { carouselCatId, advertId, product, sendTreatmentAnalytics } =
    action.payload;

  const treatment = getProductTreatment(
    { product, productIndex: product.index },
    state,
  );
  const customerId = (await getCustomerId()) ?? -1;

  return buildProductClickEvent({
    carouselCatId,
    advertId,
    customerId,
    featuredProductTileType: sendTreatmentAnalytics
      ? treatment?.treatmentType
      : null,
  });
};

emitterEvents[ANALYTICS_PAGE_LOAD] = async (_, state) => {
  const clientPageLoadData = await getClientPageLoadData(state);
  const pageSpecificAnalyticsData = getPageSpecificAnalyticsData(state);
  const appliedRefinementsData = await getAppliedRefinementsData(state, true);

  const viewItemListEvent = buildViewItemListEvent(
    pageSpecificAnalyticsData,
    clientPageLoadData,
    appliedRefinementsData,
  );

  if (viewItemListEvent) {
    pushToDataLayer({
      view_item_list: viewItemListEvent,
    });
  }

  return [
    "tracking.productList.pageLoad",
    Object.assign(
      {},
      clientPageLoadData,
      pageSpecificAnalyticsData,
      appliedRefinementsData,
    ),
  ];
};

export default emitterEvents;
